// Paddle.js (v2 Billing) lazy initialiser.
//
// Reads `REACT_APP_PADDLE_CLIENT_TOKEN` from the frontend env — if it is
// absent we return `null` so the UI can fall back to the "coming soon"
// state without crashing. Live/sandbox is picked from the token prefix
// (`live_` vs `test_`) or from `REACT_APP_PADDLE_ENV` as an override.
import { initializePaddle } from "@paddle/paddle-js";

let _paddlePromise = null;

/** Resolve the Paddle environment ("sandbox" | "production"). */
export function paddleEnvironment() {
  const override = (process.env.REACT_APP_PADDLE_ENV || "").trim().toLowerCase();
  if (override === "production" || override === "live") return "production";
  if (override === "sandbox") return "sandbox";
  const token = (process.env.REACT_APP_PADDLE_CLIENT_TOKEN || "").trim();
  return token.startsWith("live_") ? "production" : "sandbox";
}

/** Return true if a client-side token is wired in — gates the CTA buttons. */
export function paddleEnabled() {
  return Boolean((process.env.REACT_APP_PADDLE_CLIENT_TOKEN || "").trim());
}

/** Return true when Paddle checkout should be visible to EVERY visitor
 *  (not just admins). Flipped to `true` when we go live with production
 *  credentials. During the sandbox phase this stays `false` so regular
 *  visitors keep seeing "Coming soon" while admins can run real test
 *  checkouts against the sandbox catalog. */
export function paddlePublic() {
  const raw = (process.env.REACT_APP_PADDLE_PUBLIC || "").trim().toLowerCase();
  return raw === "true" || raw === "1" || raw === "yes";
}

/** Lazy singleton; safe to call multiple times. Returns a Paddle instance
 *  or null when the token is missing / init failed. */
export function getPaddle() {
  if (_paddlePromise) return _paddlePromise;
  const token = (process.env.REACT_APP_PADDLE_CLIENT_TOKEN || "").trim();
  if (!token) {
    _paddlePromise = Promise.resolve(null);
    return _paddlePromise;
  }
  _paddlePromise = initializePaddle({
    token,
    environment: paddleEnvironment(),
    checkout: {
      settings: {
        theme: "dark",
        locale: (document.documentElement.lang || "it").toLowerCase().startsWith("en") ? "en" : "it",
      },
    },
  }).catch((e) => {
    // eslint-disable-next-line no-console
    console.error("Paddle init failed:", e);
    return null;
  });
  return _paddlePromise;
}

/** Open the Paddle Checkout overlay for a single priceId.
 *  @param {object} args
 *  @param {string} args.priceId
 *  @param {string} args.plan   - internal plan key (free|singolo|basic|pro)
 *  @param {string} args.email  - signed-in user's email
 *  @param {string} args.userId - signed-in user's user_id (from /auth/me)
 *  @param {string} [args.successUrl] - absolute URL to redirect post-checkout
 */
export async function openCheckout({ priceId, plan, email, userId, successUrl, discountCode }) {
  const paddle = await getPaddle();
  if (!paddle) throw new Error("Paddle not configured");
  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: email ? { email } : undefined,
    ...(discountCode ? { discountCode } : {}),
    customData: {
      user_id: String(userId || ""),
      email: String(email || ""),
      plan: String(plan || ""),
    },
    settings: {
      displayMode: "overlay",
      theme: "dark",
      successUrl: successUrl || `${window.location.origin}/billing/success?plan=${encodeURIComponent(plan)}`,
    },
  });
}
